// src/components/exam-player/renderers/FileUploadRenderer.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { BASE_URL } from "../../../config";
// جایی که BASE_URL رو قبلاً تعریف کردی، همون‌جا رو ایمپورت کن
// مسیر زیر فقط مثاله:

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

export default function FileUploadRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const allowedExts = Array.isArray(body.allowed)
    ? body.allowed.map((s) => String(s).toLowerCase())
    : typeof body.allowed === "string"
    ? body.allowed
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    : [];

  const maxSizeMB =
    typeof body.maxSizeMB === "number" && !Number.isNaN(body.maxSizeMB)
      ? body.maxSizeMB
      : 25;

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const stemHtml =
    item?.stem?.fa || item?.stem?.en || t("preview.no_stem", "بدون متن");

  const inputRef = useRef(null);

  // --- initial value → fileInfo ---
  const initialFileInfo = useMemo(() => {
    if (!initialAnswers) return null;

    if (typeof initialAnswers === "string") {
      const parts = initialAnswers.split("/");
      const name = parts[parts.length - 1] || initialAnswers;
      return { url: initialAnswers, name };
    }

    if (isObj(initialAnswers.file)) return initialAnswers.file;

    if (isObj(initialAnswers)) {
      if (initialAnswers.url || initialAnswers.name) {
        return {
          url: initialAnswers.url,
          name: initialAnswers.name,
          size: initialAnswers.size,
          type: initialAnswers.type,
          mediaId: initialAnswers.mediaId,
          serverMimeType: initialAnswers.serverMimeType,
        };
      }
    }

    return null;
  }, [initialAnswers]);

  const [fileInfo, setFileInfo] = useState(initialFileInfo);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setFileInfo(initialFileInfo);
  }, [initialFileInfo]);

  // 🟣 اینجا لاجیک آپلود رو با نمونه‌ای که دادی سینک کردیم
  const uploadFileToServer = async (file) => {
    const fd = new FormData();
    fd.append("file", file); // باید همین name باشه
    fd.append("name", file.name); // اختیاری ولی شبیه کد خودت
    const user = JSON.parse(localStorage.getItem("user"));
    const token = user.token;

    const headers = token ? { "x-access-token": token } : undefined;

    const res = await fetch(`${BASE_URL || ""}/media`, {
      method: "POST",
      headers,
      body: fd, // ❌ Content-Type رو خود fetch ست می‌کنه، ما دستکاری نمی‌کنیم
    });

    if (!res.ok) {
      throw new Error("UPLOAD_FAILED");
    }

    const json = await res.json();
    const mediaDoc = json?.data;

    if (!mediaDoc?.url) {
      throw new Error("NO_URL_FROM_SERVER");
    }

    return {
      url: mediaDoc.url,
      name: mediaDoc.originalName || file.name,
      size: mediaDoc.size ?? file.size,
      type: mediaDoc.mimeType || file.type,
      mediaId: mediaDoc._id,
      serverMimeType: mediaDoc.mimeType,
    };
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // اجازه بده دوباره همین فایل رو هم انتخاب کنه
    e.target.value = "";

    const ext = file.name.toLowerCase().split(".").pop() || "";
    if (allowedExts.length && !allowedExts.includes(ext)) {
      toast.error(
        t("file_upload.toasts.invalid_ext", {
          ext,
          allowed: allowedExts.join(", "),
        })
      );
      return;
    }

    if (file.size > maxSizeBytes) {
      toast.error(
        t("file_upload.toasts.too_large", {
          max: maxSizeMB,
        })
      );
      return;
    }

    try {
      setUploading(true);

      const uploaded = await uploadFileToServer(file);

      const info = {
        url: uploaded.url,
        name: uploaded.name,
        size: uploaded.size,
        type: uploaded.type,
        mediaId: uploaded.mediaId,
        serverMimeType: uploaded.serverMimeType,
      };

      setFileInfo(info);
      onAnswerChange?.({ file: info });
      toast.success(
        t("file_upload.toasts.upload_success", "فایل با موفقیت آپلود شد")
      );
    } catch (err) {
      console.error(err);
      toast.error(
        t(
          "file_upload.toasts.upload_failed",
          "آپلود فایل با خطا مواجه شد. لطفاً دوباره تلاش کنید."
        )
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFileInfo(null);
    onAnswerChange?.(null);
  };

  const formatSize = (bytes) => {
    if (!bytes || typeof bytes !== "number") return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      {/* Stem */}
      <div className="prose prose-sm max-w-none mb-4">
        <div dangerouslySetInnerHTML={{ __html: stemHtml }} />
        {typeof questionNumber === "number" && (
          <div className="text-xs text-gray-500 mt-1">
            {t("preview.question_number", { n: questionNumber })}
          </div>
        )}
      </div>

      {/* محدودیت‌ها */}
      <div className="text-xs text-gray-600 mb-2">
        {allowedExts.length > 0 && (
          <div>
            {t("file_upload.allowed", "فرمت‌های مجاز")}:{" "}
            <span className="font-mono">{allowedExts.join(", ")}</span>
          </div>
        )}
        <div>
          {t("file_upload.max_size", "حداکثر حجم فایل")}:{" "}
          <span className="font-mono">{maxSizeMB} MB</span>
        </div>
      </div>

      {/* انتخاب / نمایش فایل */}
      <div className="border rounded-lg p-3 bg-gray-50">
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />

        {!fileInfo ? (
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current && inputRef.current.click()}
            className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white disabled:opacity-60"
          >
            {uploading
              ? t("file_upload.uploading", "در حال آپلود…")
              : t("file_upload.select_file", "انتخاب فایل")}
          </button>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium">{fileInfo.name}</span>
                {fileInfo.size != null && (
                  <span className="text-xs text-gray-500 ml-2">
                    {formatSize(fileInfo.size)}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="text-xs text-blue-600 underline"
                  onClick={() => inputRef.current && inputRef.current.click()}
                  disabled={uploading}
                >
                  {t("file_upload.change_file", "تغییر فایل")}
                </button>
                <button
                  type="button"
                  className="text-xs text-red-600 underline"
                  onClick={handleRemove}
                  disabled={uploading}
                >
                  {t("file_upload.remove_file", "حذف")}
                </button>
              </div>
            </div>
            {fileInfo.url && (
              <a
                href={fileInfo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 underline break-all"
              >
                {t("file_upload.open_file", "نمایش / دانلود فایل")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
